import React, { Component, Fragment } from 'react';
import gql from 'graphql-tag';
import { compose, Query } from '../../reactApolloCompat';
import { withRouter } from '../../utils/withRouter';
import withStyles from '@mui/styles/withStyles';
import Paper from '../../components/layout/Paper';
import Grid from '../../components/layout/Grid';
import DataFormView from '../../components/DataFormView';
import RecordDetailsTab from './RecordDetailsTab';
import ContactDetailsTab from './ContactDetailsTab';
import CremationActionTab from './CremationActionTab';
import BillingTab from './BillingTab';
import SummaryTab from './SummaryTab';
import CorrespondenceTab from './CorrespondenceTab';
import Button from '../../components/form/Button';
import ViewHeader from '../../components/header/ViewHeader';
import { createNewCremationOrder } from './CremationConstants';
import AlertBar from '../../components/form/AlertBar';
import { joinDefined } from '../../utils/strings';
import Spinner from '../../components/loading/Spinner';
import DocumentsIcon from '../../components/icon/DocumentsIcon';
import TemplateSelectionModal from './Modals/TemplateSelectionModal';
import Inline from '../../components/layout/Inline';
import RecordDrawer from '../../components/workQueue/RecordDrawer';
import { withErrorBoundary } from '@sentry/react';
import ErrorFallback from '../../components/ErrorFallback';
import { ExclamationCircleIcon, FireIcon } from '../../components/IconIndex';
import FlexGrid from '../../components/layout/FlexGrid';

/**
 * Tab Definition
 */
const CremationTabs = [
    RecordDetailsTab,
    ContactDetailsTab,
    CremationActionTab,
    BillingTab,
    SummaryTab,
    CorrespondenceTab
];

class Cremation extends Component {
    state = {
        showTemplateSelectionModal: false
    };

    constructor(props) {
        super(props);

        this.cremationRecord = null;
    }

    render() {
        const { match } = this.props;
        const { showTemplateSelectionModal } = this.state;
        return (
            <Grid container>
                <Query query={query} fetchPolicy="cache-and-network" variables={{ id: match.params.id }}>
                    {results => {
                        const { loading, data } = results;
                        if (loading)
                            return (
                                <div>
                                    <Spinner />
                                </div>
                            );

                        const cremation = data && data.readOneCremationOrder;

                        this.cremationRecord = cremation;

                        return this.renderContent();
                    }}
                </Query>

                <TemplateSelectionModal
                    open={showTemplateSelectionModal}
                    cremationID={match.params.id}
                    onClose={() => this.handleCloseTemplateSelectionModal()}
                />
            </Grid>
        );
    }

    renderContent() {
        const { match } = this.props;
        return (
            <Fragment>
                <Grid item>
                    {this.renderHeader()}
                    {this.renderAlerts()}
                </Grid>
                <Grid item>
                    <Paper elevation={0}>
                        <DataFormView
                            tabs={CremationTabs}
                            createNewFunc={createNewCremationOrder}
                            objectType="CremationOrder"
                            itemId={match.params.id}
                            name="Cremation"
                            onCreated={e => this.onCreateNew(e)}
                            context={this}
                            continuousValidation
                            saveNotValid
                            saveMode={1} // save all tabs
                        />
                    </Paper>
                </Grid>
            </Fragment>
        );
    }

    onCreateNew(cremationOrder) {
        const { history } = this.props;
        history.push(`cremation/${cremationOrder.LegacyKey}/${cremationOrder.ID}`);
    }

    renderHeader() {
        const cremation = this.cremationRecord;
        let subtitle = '';
        if (cremation && cremation.LegacyKey) {
            subtitle = <><strong>{cremation.LegacyKey}</strong> {joinDefined(
                [
                    cremation.Deceased.FirstName,
                    cremation.Deceased.MiddleName,
                    cremation.Deceased.Surname
                ],
                ' '
            )}</>;
        }
        return (
            <ViewHeader variant="record" title="Cremation" subtitle={subtitle}>
                <Inline center>
                    {!!cremation && (
                        <Fragment>
                            {!!cremation.SameDayCremation && (
                                <FlexGrid variant="justify-center" style={{ gap: 6, color: 'red' }}>
                                    <ExclamationCircleIcon />
                                    <small>
                                        Same-day
                                        <br />
                                        Cremation
                                    </small>
                                </FlexGrid>
                            )}
                            {'Required' === cremation.WitnessOfInsertionRequired && (
                                <FlexGrid variant="justify-center" style={{ color: '#FFA500' }}>
                                    <FireIcon />
                                    <small>
                                        Witness&nbsp;of
                                        <br />
                                        Insertion
                                    </small>
                                </FlexGrid>
                            )}
                            <Button
                                variant="primary icon-button white shadow"
                                size="lg"
                                title="Generate Merge Documents"
                                onClick={() => this.handleShowTemplateSelectionModal()}
                            >
                                <DocumentsIcon />
                            </Button>
                            <RecordDrawer cremationId={cremation.ID} legacyKey={cremation.LegacyKey} />
                        </Fragment>
                    )}
                </Inline>
            </ViewHeader>
        );
    }

    renderAlerts() {
        const cremation = this.cremationRecord;
        if (!cremation) return null;
        const approvedAllRequiredDocs = !cremation.HasMissingCertificates;
        const cancelled = cremation.CremationCancelled;
        const ready = cremation.ReadyForCremation;
        const isFromFM = cremation && cremation.Funeral && cremation.Funeral.ID;   
        return (
            <Fragment>
                {(cancelled && (
                    <AlertBar variant="error" style={{ marginBottom: -12 }}>
                        This record was cancelled.
                    </AlertBar>
                )) || ((!isFromFM && !approvedAllRequiredDocs) && (
                    <AlertBar variant="warning" style={{ marginBottom: -12 }}>
                        Attention required:
                        {!ready ? ' Unable to perform cremation because ' : ' Order marked as Cremation Ready when '}
                        important documents are missing or require review.
                    </AlertBar>
                ))}
            </Fragment>
        );
    }

    handleShowTemplateSelectionModal() {
        this.setState({ showTemplateSelectionModal: true });
    }

    handleCloseTemplateSelectionModal() {
        this.setState({ showTemplateSelectionModal: false });
    }
}

const query = gql`
    query readCremationOrderOverview($id: ID!) {
        readOneCremationOrder(ID: $id) {
            ID
            LegacyKey
            WitnessOfInsertionRequired
            SameDayCremation
            ReadyForCremation
            HasMissingCertificates
            CremationCancelled
            Deceased {
                ID
                FirstName
                MiddleName
                Surname
            }
            Funeral {
                ID
            }
        }
    }
`;

export default withErrorBoundary(compose(withRouter, withStyles({}))(Cremation), { fallback: ErrorFallback });
